import React, { useMemo, useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Provider } from 'react-redux';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { store } from './store';
import { useAppSelector } from './store/hooks';
import { enUS, es, fr, de } from 'date-fns/locale';
import AppContent from './components/AppContent';

import NotificationProvider from './components/NotificationProvider';
import ConnectionMonitorProvider from './components/ConnectionMonitorProvider';

// Create theme with clean, minimalistic design
const createAppTheme = (mode: 'light' | 'dark') =>
  createTheme({
    palette: {
      mode,
      primary: {
        main: '#a31830',
        light: '#c53030',
        dark: '#8b1426',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: '#969BA5',
        light: '#B0B5C0',
        dark: '#7A7F8A',
        contrastText: '#FFFFFF',
      },
      background: {
        default: mode === 'dark' ? '#121212' : '#FFFFFF',
        paper: mode === 'dark' ? '#1e1e1e' : '#FFFFFF',
      },
      text: {
        primary: mode === 'dark' ? '#ffffff' : '#1a1a1a',
        secondary: mode === 'dark' ? '#b0b0b0' : '#6b7280',
      },
      grey: {
        50: '#f9fafb',
        100: '#f3f4f6',
        200: '#e5e7eb',
        300: '#d1d5db',
        400: '#9ca3af',
        500: '#6b7280',
        600: '#4b5563',
        700: '#374151',
        800: '#1f2937',
        900: '#111827',
      },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica Neue", "Arial", sans-serif',
      h1: {
        fontWeight: 600,
        color: '#a31830',
        fontSize: '2.25rem',
      },
      h2: {
        fontWeight: 500,
        color: '#a31830',
        fontSize: '1.875rem',
      },
      h3: {
        fontWeight: 500,
        color: mode === 'dark' ? '#ffffff' : '#1a1a1a',
        fontSize: '1.5rem',
      },
      h4: {
        fontWeight: 400,
        color: mode === 'dark' ? '#ffffff' : '#1a1a1a',
        fontSize: '1.25rem',
      },
      h5: {
        fontWeight: 400,
        color: mode === 'dark' ? '#b0b0b0' : '#6b7280',
        fontSize: '1.125rem',
      },
      h6: {
        fontWeight: 400,
        color: mode === 'dark' ? '#ffffff' : '#1a1a1a',
        fontSize: '1rem',
      },
      body1: {
        fontWeight: 400,
        fontSize: '1rem',
        lineHeight: 1.6,
      },
      body2: {
        fontWeight: 400,
        fontSize: '0.875rem',
        lineHeight: 1.5,
      },
      button: {
        fontWeight: 500,
        textTransform: 'none',
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.875rem',
            padding: '8px 16px',
          },
          contained: {
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            '&:hover': {
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
            },
          },
          outlined: {
            borderWidth: '1px',
            '&:hover': {
              borderWidth: '1px',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            boxShadow: mode === 'dark' ? '0 1px 3px rgba(0, 0, 0, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: mode === 'dark' ? '1px solid #333333' : '1px solid #e5e7eb',
            backgroundColor: mode === 'dark' ? '#1e1e1e' : '#FFFFFF',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            boxShadow: mode === 'dark' ? '0 1px 3px rgba(0, 0, 0, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: '#FFFFFF',
            color: '#1a1a1a',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            borderBottom: '1px solid #e5e7eb',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 6,
            },
          },
        },
      },
    },
  });

function App() {
  const preferences = useAppSelector((state) => (state as any).users?.profile?.preferences);
  const mode = (preferences?.theme ?? 'light') as 'light' | 'dark';
  const language = (preferences?.language ?? 'en') as 'en' | 'es' | 'fr' | 'de';

  const dfnsLocale = useMemo(() => {
    switch (language) {
      case 'es':
        return es;
      case 'fr':
        return fr;
      case 'de':
        return de;
      default:
        return enUS;
    }
  }, [language]);

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language;
    }
  }, [language]);

  // Theme is created based on user preferences

  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dfnsLocale}>
          <Router basename={process.env.PUBLIC_URL || ''}>
            <ConnectionMonitorProvider>
              <NotificationProvider>
                <AppContent />
              </NotificationProvider>
            </ConnectionMonitorProvider>
          </Router>
        </LocalizationProvider>
      </ThemeProvider>
    </Provider>
  );
}

export default App;